import React, { useCallback, useEffect, useMemo, useState } from "react";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton, LinearProgress, Skeleton, Stack, Typography } from "@mui/material";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { TableSkeletonLoader } from "components/Table-skeleton-loader/TableSkeletonLoader";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormatDateHook } from "hooks";
import { MRT_ColumnDef, MRT_ColumnOrderState } from "material-react-table";
import { useQuickViewQueriesSelect, useReportsActions } from "store";
import { IQuicViewTableRowObj, QuickViewTableColumn, QuickViewTableRow } from "store/types";

import { useSmartSheet } from "../context/SmartSheet-context";
import { AddValueButton } from "./Cells/AddValueCell";
import { EditableCell } from "./Cells/EditableCell";
import { TextCell } from "./Cells/TextCell";
import { SmartSheetSettings } from "./Smart-sheet-settings/SmartSheetSettings";
import { SmartSheetColumnFilter } from "./SmartSheetColumnFilter";
import { SmartSheetColumnMenu } from "./SmartSheetColumnMenu";
import { Table } from "./Table";

export type SmartSheetFlatRow = Record<string, IQuicViewTableRowObj>;

enum VirtualRowType {
  Filter = "filter",
  Skeleton = "skeleton"
}

const VIRTUAL_TYPE_KEY = "__virtualType";
const FILTER_ROW_ID = VirtualRowType.Filter;

const createVirtualRow = (type: VirtualRowType): SmartSheetFlatRow =>
  ({ [VIRTUAL_TYPE_KEY]: type }) as unknown as SmartSheetFlatRow;

const getVirtualType = (row: SmartSheetFlatRow): VirtualRowType | undefined =>
  (row as Record<string, unknown>)[VIRTUAL_TYPE_KEY] as VirtualRowType | undefined;

function formatCellDisplay(
  cell: IQuicViewTableRowObj | undefined,
  formatDate: ReturnType<typeof useFormatDateHook>
): string {
  if (!cell) return "";
  if (cell.type === "date") {
    return cell.value === "-" ? "-" : formatDate({ date: cell.value ?? "" });
  }
  if (cell.postfix) {
    return cell.postfix === "%" ? `${cell.value ?? ""}%` : `${cell.postfix}${cell.value ?? ""}`;
  }
  return String(cell.value ?? cell.name ?? cell.title ?? "");
}

const SYSTEM_COLUMN_KEYS = ["investor", "round", "last_update"];
const DEAL_COLUMN_KEY = "deal";

export const SmartSheetTable: React.FC = () => {
  const {
    tableData,
    loading,
    loadingMore,
    rowToUpdate,
    fetchNextPage,
    handleCellClick,
    deleteColumn,
    refetchTable,
    containerRef,
    recentlyUpdatedCells,
    clearRecentlyUpdatedCells
  } = useSmartSheet();
  const formatDate = useFormatDateHook();
  const { setQuickViewQueriesAction } = useReportsActions();

  const [columnMenu, setColumnMenu] = useState<{
    anchorEl: HTMLElement | null;
    column?: QuickViewTableColumn;
  }>({ anchorEl: null });

  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);

  const queries = useQuickViewQueriesSelect();
  const { columnsOrder } = queries.columns;
  const visibleSystemColumns: string[] = Array.isArray(queries.columns.visibleSystemColumns)
    ? queries.columns.visibleSystemColumns
    : SYSTEM_COLUMN_KEYS;

  // Simple map: col.key → column (columnsOrder stores col.key directly)
  const colKeyToColumn = useMemo(() => {
    const allColumns = tableData?.columns ?? [];
    return new Map(allColumns.map((col) => [col.key, col]));
  }, [tableData?.columns]);

  // When server returns new columns not yet in columnsOrder — append them to the end
  useEffect(() => {
    if (!tableData?.columns?.length || columnsOrder.length === 0) return;

    const existingKeys = new Set(columnsOrder);
    const newKeys = tableData.columns.filter((col) => !existingKeys.has(col.key)).map((col) => col.key);

    if (newKeys.length > 0) {
      setQuickViewQueriesAction({
        queries: { columns: { columnsOrder: [...columnsOrder, ...newKeys] } }
      });
    }
  }, [tableData?.columns]);

  const columns = useMemo(() => {
    const allColumns = tableData?.columns ?? [];

    // If a saved order exists — apply it, but always respect visibleSystemColumns for system cols
    if (columnsOrder.length > 0) {
      const ordered = columnsOrder
        .map((key) => colKeyToColumn.get(key))
        .filter(Boolean)
        .filter((col) => {
          if (SYSTEM_COLUMN_KEYS.includes(col!.key)) return visibleSystemColumns.includes(col!.key);
          return true;
        }) as QuickViewTableColumn[];
      const orderedKeys = new Set(ordered.map((c) => c.key));
      // Append any new non-system columns not yet in columnsOrder
      const newCols = allColumns.filter(
        (c) => !orderedKeys.has(c.key) && c.key !== DEAL_COLUMN_KEY && !SYSTEM_COLUMN_KEYS.includes(c.key)
      );
      return [...ordered, ...newCols];
    }

    // Default order: deal first, then visible system columns, then the rest
    const dealColumn = allColumns.find((col) => col.key === DEAL_COLUMN_KEY);
    const systemColumns = SYSTEM_COLUMN_KEYS.filter((key) => visibleSystemColumns.includes(key))
      .map((key) => allColumns.find((col) => col.key === key)!)
      .filter(Boolean);
    const rest = allColumns.filter((col) => col.key !== DEAL_COLUMN_KEY && !SYSTEM_COLUMN_KEYS.includes(col.key));
    return [...(dealColumn ? [dealColumn] : []), ...systemColumns, ...rest];
  }, [tableData?.columns, visibleSystemColumns, columnsOrder, colKeyToColumn]);
  const rows = tableData?.rows ?? [];

  const { highlightedRowIds, highlightedCellIds } = useMemo(() => {
    const rowIds = new Set<string>();
    const cellIds = new Set<string>();
    if (recentlyUpdatedCells.size === 0) return { highlightedRowIds: rowIds, highlightedCellIds: cellIds };
    rows.forEach((row, index) => {
      const rowKey = `${row.deal?.id}-${row.investor?.id}-${row.round?.id}`;
      const mrtRowId = String(index + 1); // +1 because filter row is prepended at index 0
      recentlyUpdatedCells.forEach((cellKey) => {
        if (cellKey.startsWith(rowKey + ":")) {
          rowIds.add(mrtRowId);
          const columnKey = cellKey.slice(rowKey.length + 1);
          cellIds.add(`${mrtRowId}:${columnKey}`);
        }
      });
    });
    return { highlightedRowIds: rowIds, highlightedCellIds: cellIds };
  }, [rows, recentlyUpdatedCells]);

  const handleColumnOrderChange = useCallback(
    (updater: React.SetStateAction<MRT_ColumnOrderState>) => {
      const currentOrder = columns.map((c) => c.key);
      const rawOrder = typeof updater === "function" ? updater(currentOrder) : updater;

      // Strip MRT internal columns (mrt-row-pin, mrt-row-select, etc.)
      const filteredOrder = rawOrder.filter((key) => !key.startsWith("mrt-"));

      // deal column must always stay first — enforce it
      const dealIdx = filteredOrder.indexOf(DEAL_COLUMN_KEY);
      if (dealIdx > 0) {
        filteredOrder.splice(dealIdx, 1);
        filteredOrder.unshift(DEAL_COLUMN_KEY);
      }

      // columnsOrder stores col.key directly — no conversion needed
      setQuickViewQueriesAction({ queries: { columns: { columnsOrder: filteredOrder } } });
    },
    [columns, setQuickViewQueriesAction]
  );

  const mrtColumns = useMemo<MRT_ColumnDef<SmartSheetFlatRow>[]>(() => {
    if (!columns.length) return [];
    return columns.map((col) => ({
      id: col.key,
      accessorKey: col.key as keyof SmartSheetFlatRow,
      header: col.title,
      enableColumnDragging: col.key !== DEAL_COLUMN_KEY,
      Header: () => (
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} width="100%">
          <MaterialTooltip title={col.tooltip ?? col.title} isShow>
            <Typography noWrap>{col.title}</Typography>
          </MaterialTooltip>

          {col.key !== DEAL_COLUMN_KEY && !SYSTEM_COLUMN_KEYS.includes(col.key) && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setColumnMenu({
                  anchorEl: e.currentTarget,
                  column: col
                });
              }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Stack>
      ),
      size: col.key === "deal" ? 280 : 204,
      minSize: col.key === "deal" ? 280 : 204,
      Cell: ({ cell }) => {
        const virtualType = getVirtualType(cell.row.original);
        if (virtualType === VirtualRowType.Filter) {
          return <SmartSheetColumnFilter qvColumn={col as QuickViewTableColumn} />;
        }
        if (virtualType === VirtualRowType.Skeleton) {
          return <Skeleton animation="wave" variant="text" width="100%" />;
        }

        const value = cell.row.original[col.key];
        const row = cell.row.original;

        if (
          rowToUpdate &&
          row.deal?.id === rowToUpdate.dealId &&
          (!rowToUpdate.investorId || row.investor?.id === rowToUpdate.investorId) &&
          (!rowToUpdate.roundId || row.round?.id === rowToUpdate.roundId) &&
          col.key !== "deal" &&
          col.key !== "investor"
        ) {
          return <Skeleton animation="wave" variant="text" width="100%" />;
        }

        if (col.key === "last_update") {
          return (
            <MaterialTooltip placement="top" title={`${value?.updated_at ?? ""} / ${value?.updated_by ?? ""}`}>
              <Stack direction="column" justifyContent="center" alignItems="center">
                <Typography variant="body2">{value?.updated_at}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.7, fontSize: "0.875em" }}>
                  {value?.updated_by}
                </Typography>
              </Stack>
            </MaterialTooltip>
          );
        }

        if (col.key === "deal" && row.deal?.id) {
          const dealTitle = formatCellDisplay(value, formatDate);
          return (
            <MaterialTooltip title={dealTitle}>
              <Typography noWrap sx={{ fontSize: 14, color: "#26a6ae", lineHeight: "16px", display: "block" }}>
                <NavigateLink
                  allowNewTab
                  route="deal"
                  params={{ deal_id: row.deal.id }}
                  style={{ color: "#26a6ae", textDecoration: "none" }}
                >
                  {dealTitle}
                </NavigateLink>
              </Typography>
            </MaterialTooltip>
          );
        }

        const onCellClick = (e: React.MouseEvent<HTMLElement>) => {
          handleCellClick({
            row: cell.row.original as QuickViewTableRow,
            column: col as QuickViewTableColumn,
            anchor: e.currentTarget
          });
        };

        if (col.key === "investor" && !row[col.key].name) {
          return <AddValueButton onClick={onCellClick} />;
        }

        if (!value?.editable) {
          return <TextCell value={formatCellDisplay(value, formatDate)} />;
        }

        if (!value?.value || value.value === "-") {
          return <AddValueButton onClick={onCellClick} />;
        }
        return <EditableCell value={formatCellDisplay(value, formatDate)} onClick={onCellClick} />;
      }
    }));
  }, [columns, formatDate, handleCellClick, rowToUpdate]);

  const showSkeleton = loading && rows.length === 0 && columns.length > 0;

  const mrtData = useMemo<SmartSheetFlatRow[]>(() => {
    if (!columns.length) return [];
    const filterRow = createVirtualRow(VirtualRowType.Filter);
    if (showSkeleton) {
      const skeletonRows = Array.from({ length: 14 }, () => createVirtualRow(VirtualRowType.Skeleton));
      return [filterRow, ...skeletonRows];
    }
    return [filterRow, ...(rows as unknown as SmartSheetFlatRow[])];
  }, [rows, columns, showSkeleton]);

  // const enableRowSelection = useCallback((row: { original: SmartSheetFlatRow }) => !getVirtualType(row.original), []);

  const getRowId = useCallback(
    (row: SmartSheetFlatRow, index: number) =>
      getVirtualType(row) === VirtualRowType.Filter ? FILTER_ROW_ID : String(index),
    []
  );

  const rowPinning = useMemo(() => ({ top: [FILTER_ROW_ID] }), []);

  if (!columns.length) {
    return (
      <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>{loading && <TableSkeletonLoader rowsNum={14} />}</Box>
    );
  }

  return (
    <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
      <Table
        data={mrtData}
        columns={mrtColumns}
        onFetchNextPage={fetchNextPage}
        enableRowSelection={false}
        getRowId={getRowId}
        rowPinning={rowPinning}
        onColumnOrderChange={handleColumnOrderChange}
        lockedFirstColumnId={DEAL_COLUMN_KEY}
        highlightedRowIds={highlightedRowIds}
        highlightedCellIds={highlightedCellIds}
        onHighlightComplete={clearRecentlyUpdatedCells}
      />
      {columnMenu.anchorEl && columnMenu.column && (
        <SmartSheetColumnMenu
          item={columnMenu.column}
          anchorEl={columnMenu.anchorEl}
          onClose={() => setColumnMenu({ anchorEl: null })}
          deleteColumn={deleteColumn}
          openCustomMenu={(anchor) => setSettingsAnchor(anchor)}
          container={containerRef.current}
        />
      )}
      <SmartSheetSettings
        customMenuOpen={settingsAnchor}
        setCustomMenuOpen={setSettingsAnchor}
        onCloseWithRefetch={refetchTable}
        container={containerRef.current}
      />
      {loadingMore ? <LinearProgress sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 8 }} /> : null}
    </Box>
  );
};
